const SECTION_ID = 'quick-view-product';

class QuickView extends HTMLElement {
  connectedCallback() {
    this.dialog = this.querySelector('dialog');
    this.panel = this.querySelector('[data-quick-view-panel]');
    if (!this.dialog || !this.panel) return;

    this.skeleton = this.panel.innerHTML;
    this.controller = null;
    this.focusReturn = null;
    this.afterClose = null;
    this.busy = false;

    this.onOpen = (event) => {
      const url = event.detail?.url;
      if (url) this.open(url);
    };
    document.addEventListener('quickview:open', this.onOpen);

    this.dialog.addEventListener('click', (event) => {
      if (event.target === this.dialog || event.target.closest('[data-quick-view-close]')) this.close();
    });
    this.dialog.addEventListener('cancel', (event) => {
      event.preventDefault();
      this.close();
    });
    this.dialog.addEventListener('close', () => this.handleClosed());
    this.panel.addEventListener('change', (event) => {
      if (event.target.matches('.qv-product__radio')) this.update();
    });
    this.panel.addEventListener('submit', (event) => this.handleSubmit(event));
  }

  disconnectedCallback() {
    document.removeEventListener('quickview:open', this.onOpen);
  }

  async open(url) {
    this.controller?.abort();
    const controller = new AbortController();
    this.controller = controller;

    if (!this.dialog.open) {
      const active = document.activeElement;
      this.focusReturn = active && active !== document.body ? active : null;
      this.panel.innerHTML = this.skeleton;
      this.panel.setAttribute('aria-busy', 'true');
      this.dialog.showModal();
      Theme.lockScroll(true);
      this.dialog.querySelector('[data-quick-view-close]')?.focus({ preventScroll: true });
    } else {
      this.stopClosing();
      this.panel.setAttribute('aria-busy', 'true');
    }

    try {
      const target = new URL(url, window.location.origin);
      target.searchParams.set('section_id', SECTION_ID);
      const response = await fetch(target, { signal: controller.signal });
      if (!response.ok) throw new Error(this.dataset.errorMessage);
      const html = await response.text();
      if (controller.signal.aborted) return;
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const product = doc.querySelector('[data-qv-product]');
      if (!product) throw new Error(this.dataset.errorMessage);
      this.panel.replaceChildren(product);
      this.panel.scrollTop = 0;
      this.productUrl = target.pathname;
      this.init();
      this.panel.querySelector('#QuickViewTitle')?.focus({ preventScroll: true });
    } catch (error) {
      if (error.name === 'AbortError') return;
      this.renderError(url);
    } finally {
      if (this.controller === controller) {
        this.controller = null;
        this.panel.removeAttribute('aria-busy');
      }
    }
  }

  renderError(url) {
    const wrap = document.createElement('div');
    wrap.className = 'quick-view__error';
    const title = document.createElement('h2');
    title.id = 'QuickViewTitle';
    title.className = 'qv-product__title';
    title.tabIndex = -1;
    title.textContent = this.dataset.errorMessage || Theme.strings?.error || '';
    const link = document.createElement('a');
    link.className = 'button button--secondary';
    link.href = url;
    link.textContent = this.dataset.detailsLabel || '';
    wrap.append(title, link);
    this.panel.replaceChildren(wrap);
    title.focus({ preventScroll: true });
  }

  // Safari can't keep a closing dialog in the top layer (no `overlay` transition), so the
  // slide-out runs while it's still open and the dialog closes once it has finished.
  close() {
    this.controller?.abort();
    const dialog = this.dialog;
    if (!dialog.open || dialog.hasAttribute('data-closing')) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      dialog.close();
      return;
    }
    const finish = (event) => {
      if (event && (event.target !== dialog || event.propertyName !== 'transform')) return;
      this.stopClosing();
      if (dialog.open) dialog.close();
    };
    this.closeTimer = setTimeout(finish, 600);
    this.onCloseEnd = finish;
    dialog.addEventListener('transitionend', finish);
    dialog.setAttribute('data-closing', '');
  }

  stopClosing() {
    clearTimeout(this.closeTimer);
    if (this.onCloseEnd) this.dialog.removeEventListener('transitionend', this.onCloseEnd);
    this.onCloseEnd = null;
    this.dialog.removeAttribute('data-closing');
  }

  handleClosed() {
    this.stopClosing();
    Theme.lockScroll(Boolean(document.querySelector('dialog[open][scroll-lock]')));
    const target = this.focusReturn;
    this.focusReturn = null;
    if (target?.isConnected && target.focus) target.focus({ preventScroll: true });
    const next = this.afterClose;
    this.afterClose = null;
    this.busy = false;
    next?.();
    setTimeout(() => {
      if (!this.dialog.open) {
        this.panel.innerHTML = this.skeleton;
        this.panel.setAttribute('aria-busy', 'true');
      }
    }, 350);
  }

  init() {
    const root = this.panel;
    const node = root.querySelector('[data-product-json]');
    try {
      this.product = node ? JSON.parse(node.textContent) : null;
    } catch (error) {
      this.product = null;
    }
    this.variants = Array.isArray(this.product?.variants) ? this.product.variants : [];
    this.initGallery(root);
    this.update({ initial: true });
  }

  selectedOptions() {
    return [...this.panel.querySelectorAll('[data-option-index]')].map(
      (group) => group.querySelector('.qv-product__radio:checked')?.value ?? null
    );
  }

  currentVariant() {
    const selected = this.selectedOptions();
    if (!selected.length) return this.variants[0] || null;
    return this.variants.find((variant) => selected.every((value, index) => value === null || variant.options[index] === value)) || null;
  }

  update({ initial = false } = {}) {
    const root = this.panel;
    const form = root.querySelector('.qv-product__form');
    if (!form || !this.variants.length) return;
    const selected = this.selectedOptions();
    const variant = this.currentVariant();

    root.querySelectorAll('[data-option-index]').forEach((group) => {
      const index = Number(group.dataset.optionIndex);
      const value = selected[index];
      const label = group.querySelector('[data-option-selected]');
      if (label) label.textContent = value ?? '';
      group.querySelectorAll('.qv-product__radio').forEach((input) => {
        const pill = input.nextElementSibling;
        const available = this.variants.some(
          (item) => item.available && item.options.every((option, i) => (i === index ? option === input.value : !selected[i] || selected[i] === option))
        );
        pill?.classList.toggle('is-unavailable', !available);
        const hint = pill?.querySelector('[data-sold-out-text]');
        if (hint) hint.hidden = available;
      });
    });

    const idInput = form.querySelector('[data-variant-id]');
    if (idInput) idInput.value = variant ? variant.id : '';

    const price = root.querySelector('[data-quick-price]');
    if (price && variant) price.innerHTML = this.priceHtml(variant);

    const submit = form.querySelector('[type="submit"]');
    if (submit) {
      const available = Boolean(variant?.available);
      submit.disabled = !available;
      submit.textContent = available ? submit.dataset.addLabel : variant ? submit.dataset.soldOutLabel : submit.dataset.unavailableLabel || submit.dataset.soldOutLabel;
    }
    this.setError('');

    const mediaId = variant?.featured_media?.id;
    if (mediaId && !initial) {
      const slide = root.querySelector(`[data-media-id="${mediaId}"]`);
      const track = root.querySelector('[data-gallery-track]');
      if (slide && track) track.scrollTo({ left: slide.offsetLeft - track.offsetLeft - (parseFloat(getComputedStyle(track).paddingLeft) || 0), behavior: 'smooth' });
    }

    if (variant) {
      document.dispatchEvent(new CustomEvent('variant:change', { detail: { variant, sectionId: SECTION_ID } }));
    }
  }

  priceHtml(variant) {
    const price = Theme.formatMoney(variant.price);
    if (variant.compare_at_price > variant.price && !document.documentElement.hasAttribute('data-hide-compare')) {
      const template = this.panel.querySelector('[data-quick-price]')?.dataset.saveTemplate || '';
      const save = window.ConvertGifts?.formatShortMoney
        ? window.ConvertGifts.formatShortMoney(variant.compare_at_price - variant.price, Theme.formatMoney, Theme.moneyFormat)
        : Theme.formatMoney(variant.compare_at_price - variant.price);
      const badge = template ? `<span class="qv-product__save">${template.replace('[amount]', save)}</span>` : '';
      return `<span class="qv-product__price-current text-sale">${price}</span><s class="qv-product__price-compare">${Theme.formatMoney(variant.compare_at_price)}</s>${badge}`;
    }
    return `<span class="qv-product__price-current">${price}</span>`;
  }

  setError(message) {
    const slot = this.panel.querySelector('[data-quick-error]');
    if (!slot) return;
    slot.textContent = message;
    slot.hidden = !message;
  }

  handleSubmit(event) {
    const form = event.target.closest('.qv-product__form');
    if (!form) return;
    event.preventDefault();
    const variant = this.currentVariant();
    const button = form.querySelector('[type="submit"]');
    if (!variant?.available || this.busy) return;
    this.busy = true;
    this.setError('');
    button?.setAttribute('aria-busy', 'true');
    const item = { id: Number(variant.id), quantity: 1 };
    const sellingPlan = form.querySelector('[name="selling_plan"]')?.value;
    if (sellingPlan) item.selling_plan = Number(sellingPlan);
    Theme.cart
      .add([item], { source: 'quick-view', toast: false })
      .then(() => {
        this.afterClose = () => {
          if (document.querySelector('cart-page')) Theme.toast(Theme.strings?.added);
        };
        this.close();
      })
      .catch((error) => {
        this.busy = false;
        this.setError(error?.message || Theme.strings?.error || '');
      })
      .finally(() => button?.removeAttribute('aria-busy'));
  }

  initGallery(root) {
    const track = root.querySelector('[data-gallery-track]');
    const dots = root.querySelector('[data-gallery-dots]');
    if (!track || !dots) return;
    const slides = [...track.querySelectorAll('[data-gallery-slide]')];
    const template = dots.dataset.slideLabel || '';
    dots.style.setProperty('--dot-count', String(slides.length));
    dots.replaceChildren(
      ...slides.map((slide, index) => {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'qv-product__dot';
        button.setAttribute('aria-label', template.replace('[index]', String(index + 1)));
        button.setAttribute('aria-current', String(index === 0));
        button.addEventListener('click', () => {
          const padding = parseFloat(getComputedStyle(track).paddingLeft) || 0;
          track.scrollTo({ left: slide.offsetLeft - track.offsetLeft - padding, behavior: 'smooth' });
        });
        return button;
      })
    );
    let frame = 0;
    track.addEventListener(
      'scroll',
      () => {
        cancelAnimationFrame(frame);
        frame = requestAnimationFrame(() => {
          const width = slides[0]?.getBoundingClientRect().width || track.clientWidth;
          const index = Math.min(slides.length - 1, Math.round(track.scrollLeft / Math.max(1, width)));
          dots.querySelectorAll('.qv-product__dot').forEach((dot, i) => dot.setAttribute('aria-current', String(i === index)));
        });
      },
      { passive: true }
    );
  }
}

if (!customElements.get('quick-view')) customElements.define('quick-view', QuickView);
